/* eslint-disable consistent-return */
/**
 * Tasks V2 Migration — Mirror Activity Feed slice (Slice 1 + 2).
 *
 * Backs MirrorActivityFeedPanel (the per-row write feed) and
 * MirrorLineageDrawer (the per-record lineage view that opens when an
 * operator clicks an activity row OR drills in from a lead/task).
 *
 * Why a dedicated slice (and NOT bolted on to tasksV2Migration.js):
 *   - The feed paginates and filters independently of every other panel
 *     in the cockpit. Mixing into the omnibus slice would cause every
 *     filter toggle to dirty unrelated reducers.
 *   - The lineage drawer is keyed by (kind, id) and may be opened from
 *     contexts outside the cockpit (lead detail, task list); a separate
 *     slice keeps that data layer reachable without dragging in the
 *     full cockpit slice.
 *
 * State shape:
 *   {
 *     activity: {
 *       byPractice: {
 *         [practiceId]: {
 *           rows: [...],
 *           count: number,            // total matching server-side
 *           filters: { direction, state, since },
 *           fetchedAt: ISOString,
 *           loading: bool,
 *           error: string | null,
 *         }
 *       }
 *     },
 *     lineage: {
 *       byKey: {
 *         // key = `${kind}:${id}` where kind = 'reminder' | 'task'
 *         [key]: {
 *           ledger: {...} | null,
 *           outbox: [...],
 *           parity_verdict: {...} | null,
 *           fetchedAt: ISOString,
 *           loading: bool,
 *           error: string | null,
 *         }
 *       }
 *     }
 *   }
 *
 * TODO(integrator): register this slice in src/redux/store.js under the
 * key `tasksV2MirrorActivity`. The panel and drawer read from
 * state.tasksV2MirrorActivity.activity / .lineage respectively.
 */
import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { callAdminApi } from '../../utils/apiCall';

const BASE = '/api/v1/management/tasks-migration';

const emptyActivityState = () => ({
    rows: [],
    count: 0,
    filters: { direction: 'all', state: 'all', since: null },
    // soakStatus is `null` for any practice not currently in front_mirror,
    // and otherwise a {current_phase, target_phase, eligible,
    // unmet_conditions} block from the BE — see
    // _build_soak_status_payload in tasks_migration_views.py.
    soakStatus: null,
    fetchedAt: null,
    loading: false,
    error: null,
});

const emptyLineageState = () => ({
    ledger: null,
    outbox: [],
    parity_verdict: null,
    fetchedAt: null,
    loading: false,
    error: null,
});

const initialState = {
    activity: { byPractice: {} },
    lineage: { byKey: {} },
};

const slice = createSlice({
    name: 'tasksV2MirrorActivity',
    initialState,
    reducers: {
        // ---- Activity feed ----
        setActivityLoading(state, action) {
            const { practiceId, value } = action.payload;
            if (!state.activity.byPractice[practiceId]) {
                state.activity.byPractice[practiceId] = emptyActivityState();
            }
            state.activity.byPractice[practiceId].loading = value;
            if (value) state.activity.byPractice[practiceId].error = null;
        },
        setActivityError(state, action) {
            const { practiceId, error } = action.payload;
            if (!state.activity.byPractice[practiceId]) {
                state.activity.byPractice[practiceId] = emptyActivityState();
            }
            state.activity.byPractice[practiceId].error = error;
            state.activity.byPractice[practiceId].loading = false;
        },
        setActivityRows(state, action) {
            const {
                practiceId, rows, count, filters, fetchedAt, soakStatus,
            } = action.payload;
            state.activity.byPractice[practiceId] = {
                rows: rows || [],
                count: count ?? (rows?.length || 0),
                filters: filters || { direction: 'all', state: 'all', since: null },
                soakStatus: soakStatus ?? null,
                fetchedAt: fetchedAt || new Date().toISOString(),
                loading: false,
                error: null,
            };
        },
        clearActivity(state, action) {
            const { practiceId } = action.payload || {};
            if (practiceId) {
                delete state.activity.byPractice[practiceId];
            } else {
                state.activity.byPractice = {};
            }
        },

        // ---- Lineage drawer ----
        setLineageLoading(state, action) {
            const { key, value } = action.payload;
            if (!state.lineage.byKey[key]) {
                state.lineage.byKey[key] = emptyLineageState();
            }
            state.lineage.byKey[key].loading = value;
            if (value) state.lineage.byKey[key].error = null;
        },
        setLineageError(state, action) {
            const { key, error } = action.payload;
            if (!state.lineage.byKey[key]) {
                state.lineage.byKey[key] = emptyLineageState();
            }
            state.lineage.byKey[key].error = error;
            state.lineage.byKey[key].loading = false;
        },
        setLineagePayload(state, action) {
            const { key, payload } = action.payload;
            state.lineage.byKey[key] = {
                ledger: payload?.ledger || null,
                outbox: payload?.outbox || [],
                parity_verdict: payload?.parity_verdict || null,
                fetchedAt: payload?.fetched_at || new Date().toISOString(),
                loading: false,
                error: null,
            };
        },
        clearLineage(state, action) {
            const { key } = action.payload || {};
            if (key) {
                delete state.lineage.byKey[key];
            } else {
                state.lineage.byKey = {};
            }
        },
    },
});

export default slice.reducer;
export const {
    setActivityLoading,
    setActivityError,
    setActivityRows,
    clearActivity,
    setLineageLoading,
    setLineageError,
    setLineagePayload,
    clearLineage,
} = slice.actions;

/**
 * fetchMirrorActivity(practiceId, { direction, state, since, limit })
 *   GET /api/v1/management/tasks-migration/practices/{id}/mirror-activity/
 *
 * Defaults: direction='all', state='all', limit=200. Backend caps limit
 * at 500. The activity feed is meant to be auto-refreshed every 30s by
 * the panel, so we deliberately skip a redux-level cache — the BE owns
 * cadence, the FE just re-fetches.
 */
export function fetchMirrorActivity(
    practiceId,
    { direction = 'all', state = 'all', since = null, limit = 200 } = {},
) {
    return async () => {
        if (!practiceId) return null;
        const filters = { direction, state, since };
        try {
            dispatch(slice.actions.setActivityLoading({ practiceId, value: true }));
            const params = new URLSearchParams();
            if (direction && direction !== 'all') params.set('direction', direction);
            if (state && state !== 'all') params.set('state', state);
            if (since) params.set('since', since);
            if (limit) params.set('limit', String(limit));
            const qs = params.toString();
            const url = `${BASE}/practices/${practiceId}/mirror-activity/${qs ? `?${qs}` : ''}`;
            const response = await callAdminApi(url, 'GET', null, { timeout: 15000 });
            if (response.status === 200) {
                // BE may return either {results, count, soak_status}
                // (paginated) or a bare array on the unpaginated
                // fallback path. Normalise.
                const data = response.data || {};
                const rows = Array.isArray(data) ? data : (data.results || []);
                const count = Array.isArray(data)
                    ? rows.length
                    : (data.count ?? rows.length);
                const soakStatus = Array.isArray(data)
                    ? null
                    : (data.soak_status ?? null);
                dispatch(slice.actions.setActivityRows({
                    practiceId,
                    rows,
                    count,
                    filters,
                    soakStatus,
                    fetchedAt: new Date().toISOString(),
                }));
            } else if (response.status === 0) {
                dispatch(slice.actions.setActivityError({
                    practiceId,
                    error: 'Network timeout fetching mirror activity.',
                }));
            } else {
                const detail = response.data?.detail
                    || `Failed to fetch mirror activity (HTTP ${response.status})`;
                dispatch(slice.actions.setActivityError({ practiceId, error: detail }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setActivityError({ practiceId, error: message }));
            return { ok: false, status: 0, error };
        }
    };
}

/**
 * fetchMirrorLineage(practiceId, { kind, id })
 *   GET /api/v1/management/tasks-migration/practices/{id}/mirror-lineage/?reminder_id=...
 *   GET /api/v1/management/tasks-migration/practices/{id}/mirror-lineage/?task_id=...
 *
 * kind: 'reminder' | 'task'. Stored under the cache key `${kind}:${id}`
 * so opening the same drawer twice in the same session reuses state.
 *
 * NOTE: the backing endpoint is built in task #4 (mirror-lineage). Until
 * that lands, this thunk will surface a 404 from the BE — the drawer
 * handles the error state.
 */
export function fetchMirrorLineage(practiceId, { kind, id } = {}) {
    return async () => {
        if (!practiceId || !kind || !id) return null;
        const key = `${kind}:${id}`;
        try {
            dispatch(slice.actions.setLineageLoading({ key, value: true }));
            const params = new URLSearchParams();
            if (kind === 'reminder') params.set('reminder_id', String(id));
            else if (kind === 'task') params.set('task_id', String(id));
            const url = `${BASE}/practices/${practiceId}/mirror-lineage/?${params.toString()}`;
            const response = await callAdminApi(url, 'GET', null, { timeout: 15000 });
            if (response.status === 200) {
                dispatch(slice.actions.setLineagePayload({
                    key,
                    payload: response.data,
                }));
            } else if (response.status === 0) {
                dispatch(slice.actions.setLineageError({
                    key,
                    error: 'Network timeout fetching lineage.',
                }));
            } else {
                const detail = response.data?.detail
                    || `Failed to fetch lineage (HTTP ${response.status})`;
                dispatch(slice.actions.setLineageError({ key, error: detail }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setLineageError({ key, error: message }));
            return { ok: false, status: 0, error };
        }
    };
}
